import { useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Row {
  key: string;
  name: string;
  blurb: string;
}

const PATHS: Row[] = [
  {
    key: 'BASE_PRICE',
    name: 'Base price override',
    blurb:
      'Sets the subscription’s base price directly, replacing whatever the catalog (module → bundle → region → pricing rule) would otherwise calculate. Lives on the subscription itself as a basePriceOverride snapshot — the pre-override list value, the override value, the effective mode, and the effective date are all recorded.',
  },
  {
    key: 'COMPONENT_RATE',
    name: 'Component rate override',
    blurb:
      'Writes a SUBSCRIPTION-scoped Variable Component Price for this subscription. It doesn’t touch the subscription document at all — it slots into the same resolver chain used everywhere else, at the most specific scope. Because it wins that chain outright it must carry the component’s whole price, not just the figure you want to change.',
  },
];

interface Shape {
  key: string;
  model: string;
  needs: string;
}

const SHAPES: Shape[] = [
  { key: 'FLAT_RATE', model: 'Flat rate', needs: 'One price per unit.' },
  {
    key: 'TIERED',
    model: 'Volume tiered · Graduated tiered',
    needs:
      'One rate per tier, in the component’s tier order — all of them. A single rate is not a tier schedule, and one missing row is not a price.',
  },
  {
    key: 'STAIRSTEP',
    model: 'Stairstep',
    needs: 'One flat fee per step, one for every band the component defines.',
  },
  { key: 'TRUE_OVERAGE', model: 'True overage', needs: 'One overage price per unit.' },
  {
    key: 'COMMITTED_OVERAGE',
    model: 'Committed overage',
    needs: 'Both halves — the committed price per unit and the overage price per unit.',
  },
  {
    key: 'COMMISSION',
    model: 'Commission',
    needs:
      'Nothing is required: the percentage rate is structural, living on the component itself. The fixed fee, minimum, and maximum per transaction are optional adjustments.',
  },
  {
    key: 'PROMOTIONAL',
    model: 'Promotional',
    needs:
      'A fixed-amount promotion needs its discount amount. A percentage one is fully described by the component and needs no money at all.',
  },
];

const MODES: Row[] = [
  {
    key: 'PROSPECTIVE',
    name: 'Prospective',
    blurb: 'Takes effect at the subscription’s next renewal. The current cycle bills as already scheduled.',
  },
  {
    key: 'IMMEDIATE_PRORATED',
    name: 'Immediate, prorated',
    blurb:
      'Takes effect right away. A correction invoice is generated for the remainder of the current cycle, prorated the same way a mid-cycle expansion would be.',
  },
];

export function PriceOverrideGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Overriding base and component pricing — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Overriding base &amp; component pricing</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Two prices, <em>two</em> override paths.
        </h1>
        <p className="dp-oneliner">
          A subscription’s bill is made of a base price and zero or more Variable Component
          charges. Each has its own override mechanism &mdash; they don’t share a code path,
          and a Custom Pricing Agreement never touches the base price.
        </p>
      </header>

      <section className="dp-section">
        <h2>Two different targets</h2>
        <p className="dp-plain">
          The base price comes from the catalog: module → bundle → region → pricing
          rule. Variable Component prices come from the{' '}
          <Link to="/docs/guides/scoped-pricing">scope resolver</Link>, most-specific first
          (Subscription → Bundle → Module → Global default), with a{' '}
          <Link to="/docs/guides/custom-agreements">Custom Pricing Agreement</Link> able to sit
          above all of it. Overriding one has no effect on the other &mdash; they are separate
          calculations that happen to land on the same invoice.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          {PATHS.map((p) => (
            <div className="dp-field-row" key={p.key}>
              <div className="dp-field-k">{p.name}</div>
              <div className="dp-field-v">{p.blurb}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          A Custom Pricing Agreement’s <code>overrideScope: PRICE</code> axis only ever
          reaches a Variable Component. If you need to change the base price for one customer, use
          the base price override on their subscription instead — it is a separate flow, found
          on the subscription’s edit screen rather than in the Custom Pricing Agreement editor.
        </p>
      </section>

      <section className="dp-section">
        <h2>What a component rate override has to carry</h2>
        <p className="dp-section-sub">
          A scope replaces the resolved price. It does not merge into it.
        </p>
        <p className="dp-plain">
          A Variable Component splits in two: the component owns the <em>structure</em> of
          its model — tier and step boundaries, included units, committed quantity, the
          commission percentage — and a{' '}
          <Link to="/docs/concepts/variableComponentPrice">Variable Component Price</Link>{' '}
          owns the money. A Subscription-scoped override is a whole price at the most
          specific scope, so it stands in for the catalog row it beats rather than patching
          it. That means it has to carry every monetary field the component’s model bills
          on:
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          {SHAPES.map((s) => (
            <div className="dp-field-row" key={s.key}>
              <div className="dp-field-k">{s.model}</div>
              <div className="dp-field-v">{s.needs}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The override surfaces — the create-subscription wizard and the edit-subscription
          modal — render the inputs for whichever of these the component needs, so a tiered
          component asks for a rate per tier and a stairstep one asks for a fee per band. A
          partly filled grid blocks the save; it is not billable, and storing it would only
          fail later, on every invoice and every revenue calculation that touched the
          subscription. The same check applies to prices authored at the Bundle, Module, and
          Global default scopes.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Allowances are a separate edit.</strong> Changing only a component’s
          included units or free allowance on a subscription leaves its price alone. It does
          not rewrite the resolved rate back as an override — which, for a tiered or
          stairstep component, would collapse the whole schedule into whichever single rate
          happened to apply.
        </p>
      </section>

      <section className="dp-section">
        <h2>When it takes effect</h2>
        <p className="dp-section-sub">Both override paths support the same two effective modes.</p>
        <div className="dp-fields-table">
          {MODES.map((m) => (
            <div className="dp-field-row" key={m.key}>
              <div className="dp-field-k">{m.name}</div>
              <div className="dp-field-v">{m.blurb}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Immediate, prorated overrides emit a real correction invoice through the same expansion
          machinery as a mid-cycle upgrade &mdash; there is no separate "override invoice" type,
          it is simply an expansion whose cause is the price change rather than a plan change.
        </p>
      </section>

      <section className="dp-section">
        <h2>No catalog guardrails</h2>
        <p className="dp-plain">
          Overrides are an explicit escape hatch: there is no minimum, maximum, or sanity check
          against the catalog list price. Setting a base price override to $0, or a component rate
          override far above or below the published rate, is allowed. Use a{' '}
          <Link to="/docs/guides/discounting">discount</Link> instead if you want a bounded,
          named, auditable percentage or fixed reduction rather than a raw rate replacement.
        </p>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/scoped-pricing" style={{ color: 'inherit', textDecoration: 'none' }}>
              Scope-based pricing ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/custom-agreements" style={{ color: 'inherit', textDecoration: 'none' }}>
              Custom Pricing Agreements ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/discounting" style={{ color: 'inherit', textDecoration: 'none' }}>
              Flexible discounting ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
